
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

export const Categories = {
    Financial: 'Financial',
    Malpractice: 'Malpractice',
    Resource: 'Resource',
    Strategic: 'Strategic',
    Temperament: 'Temperament'
};

export type CategoriesType = typeof Categories[keyof typeof Categories];
 /**
 * RedFlagType
 *
 * A red flag type is a configurable risk indicator definition used during client intake (leads) and active case management. It specifies the warning text, a risk weight (percentage contribution to an overall risk score), optional case-type scoping, and categories (Financial, Malpractice, Resource, Strategic, Temperament). Firms configure a library of these types; individual leads and cases record checked/boosted instances against them via the RED_FLAG table.
 */
class RedFlagType extends Base implements EntityType {
    redFlagTypeId: uuid;
    appliesTo: uuid[];
    position: number;
    name: string;
    weight: number;
    categories: CategoriesType[];
    icon?: string | null;

    /**
     * Create a new RedFlagType, with an (optional) JSON object or RedFlagType class
     *
     * @param {Object|RedFlagType} doc - Can be either a simple JSON object or a RedFlagType class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof RedFlagType | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            redFlagTypeId: genUUID(),
            position: null,
            name: null,
            weight: 100,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'redFlagType';
        // @ts-ignore
        this.redFlagTypeId = doc._redFlagTypeId || doc.redFlagTypeId || doc.redflagtypeid || genUUID();
        // @ts-ignore
        this.appliesTo = doc._appliesTo || doc.appliesTo || doc.appliesto || [];
        // @ts-ignore
        this.position = util.coalesce(util.parseInt(doc._position), util.parseInt(doc.position), null);
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.weight = util.coalesce(util.parseFloat(doc._weight), util.parseFloat(doc.weight), 100);
        // @ts-ignore
        this.categories = doc._categories || doc.categories || [];
        // @ts-ignore
        this.icon = doc._icon || doc.icon || null;

    }
    /**
     * @returns {uuid} This is a generic pointer to the redFlagTypeId across all entity types
     */
    get id(): uuid {
        return this.redFlagTypeId;
    }


    /**
     * Add a appliesTo to redFlagType.appliesTo
     *
     * @param {uuid} appliesTo - The AppliesTo to add
     * @returns uuid - The appliesTo with any defaulted fields set
     */
    addAppliesTo(appliesTo: uuid) : uuid {
        if (!appliesTo) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.appliesTo) this.appliesTo = [];
        this.appliesTo.push(appliesTo);

        return appliesTo;
    }

    /**
     * Update a appliesTo from redFlagType
     *
     * @param {uuid} appliesTo - The AppliesTo to remove
     */
    updateAppliesTo(appliesTo: uuid) {
        if (!this.appliesTo) this.appliesTo = [];
        
        // Find the matching object by appliesTo.appliesToId
        const index = this.appliesTo.findIndex(item => item === appliesTo);
        if (index !== -1) {
            this.appliesTo[index] = appliesTo;
        }
        else this.addAppliesTo(appliesTo);
    }

    /**
     * Remove a appliesTo from redFlagType
     *
     * @param {uuid} appliesTo - The AppliesTo to remove
     */
    removeAppliesTo(appliesTo: uuid) {
        if (!this.appliesTo) return;
        
        // Find the matching object by appliesTo.appliesToId
        let foundItems = this.appliesTo?.filter(item => item === appliesTo);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.appliesTo = this.appliesTo.filter(item => !(item === appliesTo));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonAppliesTo() {
        this.appliesTo = [];
     }
        
    static CATEGORIES_VALUES = ['Financial', 'Malpractice', 'Resource', 'Strategic', 'Temperament'];
    
    /**
     * Add a category to redFlagType.categories
     *
     * @param {string} category - The Category to add
     * @returns string - The category with any defaulted fields set
     */
    addCategory(category: string) : string {
        if (!category) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.categories) this.categories = [];
        this.categories.push(category);

        return category;
    }

    /**
     * Update a category from redFlagType
     *
     * @param {string} category - The Category to remove
     */
    updateCategory(category: string) {
        if (!this.categories) this.categories = [];
        
        // Find the matching object by category.categoryId
        const index = this.categories.findIndex(item => item === category);
        if (index !== -1) {
            this.categories[index] = category;
        }
        else this.addCategory(category);
    }

    /**
     * Remove a category from redFlagType
     *
     * @param {string} category - The Category to remove
     */
    removeCategory(category: string) {
        if (!this.categories) return;
        
        // Find the matching object by category.categoryId
        let foundItems = this.categories?.filter(item => item === category);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.categories = this.categories.filter(item => !(item === category));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonCategories() {
        this.categories = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this redFlagType"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.redFlagTypeId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["appliesTo", "categories"];
    }

    /**
     * Default entries are Outlaw-supplied RedFlagType entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): RedFlagType[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'redFlagType',
            table: 'RED_FLAG_TYPE',
            comment: 'A red flag type is a configurable risk indicator definition used during client intake (leads) and active case management. It specifies the warning text, a risk weight (percentage contribution to an overall risk score), optional case-type scoping, and categories (Financial, Malpractice, Resource, Strategic, Temperament). Firms configure a library of these types; individual leads and cases record checked/boosted instances against them via the RED_FLAG table.',
            fields: {
                redFlagTypeId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique id for this redFlagType'
                },
                appliesTo: {
                    type: 'uuid[]',
                    entityType: 'caseType',
                    required: false,
                    comment: 'The case type(s) this red flag applies to. No entry means it applies to all case types.'
                },
                position: {
                    type: 'int',
                    required: true,
                    comment: 'position in the list (1-n)'
                },
                name: {
                    type: 'string',
                    required: true,
                    comment: 'The text of the flag'
                },
                weight: {
                    type: 'float',
                    required: true,
                    default: 100.0,
                    comment: 'How much this warning counts toward the risk score, as a percentage (100 = normal weight)'
                },
                categories: {
                    type: 'string[]',
                    required: false,
                    values: ['Financial', 'Malpractice', 'Resource', 'Strategic', 'Temperament'],
                    comment: 'What categories does this fall under?'
                },
                // Transients
                icon: {
                    type: 'string',
                    transient: true,
                    comment: 'Icon for this red flag'
                }
            },
            indexes: [{
                    name: 'leads',
                    fn: m => m.entityType === 'lead' && m.status !== 'D'
                },
                {
                    name: 'matters',
                    fn: m => m.entityType === 'matter' && m.status !== 'D'
                },
            ],
            maps: [{
                    name: 'entityType',
                    keysFn: t => t.entityType,
                    presentFn: (t, k) => t.entityType === k
                },
                {
                    name: 'appliesTo',
                    keysFn: t => t.appliesTo,
                    presentFn: (t, k) => t.appliesTo?.includes(k)
                }
            ]
        });
    }

}
export default RedFlagType;
